import React, { useEffect, useState, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS } from 'react-joyride';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { getTourSteps } from './TourSteps';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';

interface OnboardingTourProps {
  run?: boolean;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ run = false }) => {
  const {
    currentStep,
    totalSteps,
    isOnboardingActive,
    nextStep,
    skipOnboarding,
    completeOnboarding,
    userRole
  } = useOnboarding();

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentMode, currentBusinessType } = useNavigation();

  // Use currentMode for tour steps (respects admin mode switching)
  const tourRole = currentMode || userRole;
  const [steps, setSteps] = useState(getTourSteps(tourRole));
  const [joyrideRun, setJoyrideRun] = useState(false); // Start with false, will be set when ready
  const [tourStarted, setTourStarted] = useState(false); // Prevent multiple tour starts

  // Function to wait for target element and start tour
  const waitForElementAndStartTour = useCallback((targetSelector: string) => {
    let timeoutId: NodeJS.Timeout | null = null;
    let checkCount = 0;
    const maxChecks = 100; // 100 * 100ms = 10 seconds

    const checkElement = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        // Element is found, start/resume the tour
        console.log(`OnboardingTour: Element ${targetSelector} found. Starting tour.`);
        setJoyrideRun(true);
      } else {
        checkCount++;
        // Element not found yet, check again
        if (checkCount < maxChecks) {
          timeoutId = setTimeout(checkElement, 100);
        } else {
          // Timeout - try to run anyway or log error
          console.warn(`OnboardingTour: Element ${targetSelector} not found after 10 seconds.`);
          // Try to show it anyway, maybe it's there but hidden or issue with selector
          setJoyrideRun(true);
        }
      }
    };

    // Start checking
    checkElement();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Update steps when tour role changes
  useEffect(() => {
    const newSteps = getTourSteps(tourRole);
    setSteps(newSteps);
  }, [tourRole]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (joyrideRun || isOnboardingActive)) {
        event.preventDefault();
        event.stopPropagation();
        skipOnboarding();
      }
    };

    if (joyrideRun || isOnboardingActive) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [joyrideRun, isOnboardingActive, skipOnboarding]);


  // Effect to manage step transitions and navigation
  useEffect(() => {
    if (!isOnboardingActive || !steps.length) {
      setJoyrideRun(false);
      setTourStarted(false);
      return;
    }

    // Don't do anything if tour is running and we are stable
    // We only want to intervene if we need to navigate or wait for an element

    const currentStepPath = getNavigationPath(currentStep, tourRole);
    const currentStepData = steps[currentStep];

    // Safety check
    if (!currentStepData) return;

    // Check if we need to navigate
    if (location.pathname !== currentStepPath) {
      // If we need to navigate, ensure tour is hidden
      if (joyrideRun) setJoyrideRun(false);

      console.log(`OnboardingTour: Navigating to ${currentStepPath} for step ${currentStep}`);
      navigate(currentStepPath);
      // After navigate, we return and let the next effect cycle handle the waiting
      return;
    }

    // If we are on the right path, but tour is not running (e.g. after nav), wait for element
    if (!joyrideRun) {
      const targetSelector = typeof currentStepData.target === 'string'
        ? currentStepData.target
        : `[data-tour="${currentStepData.target}"]`;

      console.log(`OnboardingTour: On correct path, waiting for ${targetSelector}`);
      waitForElementAndStartTour(targetSelector);
    }

  }, [currentStep, isOnboardingActive, location.pathname, steps, tourRole, waitForElementAndStartTour]);

  // Effect to handle programmatic interactions (like clicking tabs)
  useEffect(() => {
    if (!isOnboardingActive || tourRole !== 'business') return;

    const performInteraction = () => {
      // Indices for 'business' tour (0-based) - Shifted +1 due to new Sidebar step
      // 6: Manage Availability (Button Highlight)
      // 14: AI General
      // 15: AI Prompts
      // 16: AI Hours

      // Reset to Calendar view if we are on calendar view steps
      // Indices 4 (Views) and 5 (Grid) require the calendar tab.
      if (currentStep >= 4 && currentStep <= 5) {
        const tab = document.querySelector('[data-tour="business-calendar-tab-calendar"]') as HTMLButtonElement;
        if (tab) tab.click();
      }


    };

    // Run interaction with a slight delay to ensure DOM is ready
    const timer = setTimeout(performInteraction, 500);
    return () => clearTimeout(timer);

  }, [currentStep, isOnboardingActive, tourRole]);

  const getNavigationPath = (stepIndex: number, role: string | null): string => {
    // For admin users, use the current mode for navigation paths
    const effectiveRole = (role === 'admin' && currentMode !== 'consultant') ? currentMode : role;

    const basePaths = {
      manager: '/manager',
      consultant: '/consultant',
      client: '/clientportal',
      business: `/business/${currentBusinessType || 'other'}`
    };

    const basePath = basePaths[effectiveRole as keyof typeof basePaths] || '/consultant';

    // Define step-to-route mapping for each role
    const stepRoutes = {
      manager: [
        '/manager/dashboard', // dashboard-welcome
        '/manager/dashboard', // header-sidebar
        '/manager/dashboard', // header-project-selector
        '/manager/dashboard', // header-notifications
        '/manager/dashboard', // header-messages
        '/manager/dashboard', // header-notes-icon
        '/manager/dashboard', // header-pomodoro-timer
        '/manager/dashboard', // header-three-dots-menu
        '/manager/dashboard', // header-avatar
        '/manager/dashboard', // projects-section
        '/manager/dashboard', // upcoming-items
        '/manager/dashboard', // project-tasks
        '/manager/dashboard', // recent-activity
        '/manager/dashboard', // project-team
        '/manager/dashboard', // assigned-clients
        '/manager/projects', // create-project (projects page)
        '/manager/projects', // create-project-button
        '/manager/projects', // available consultants section
        '/manager/projects', // available clients section
        '/manager/calendar', // calendar-section
        '/manager/research/conversationalAI', // conversational ai intro
        '/manager/research/conversationalAI', // ai-chat-menu-toggle
        '/manager/research/conversationalAI', // ai-chat-new-session
        '/manager/research/conversationalAI', // ai-chat-file-panel
        '/manager/research/conversationalAI', // ai-chat-message-input
        '/manager/research/conversationalAI', // ai-chat-attach-files
        '/manager/research/conversationalAI', // ai-chat-voice-input
        '/manager/research/conversationalAI', // ai-chat-send-message
        '/manager/research/insights', // video insights intro
        '/manager/research/insights', // video-browse
        '/manager/research/insights', // video-saved-insights
        '/manager/research/insights', // video-url-input
        '/manager/research/insights', // video-transcript-tab
        '/manager/research/insights', // video-minutes-tab
        '/manager/research/insights', // video-summary-tab
        '/manager/research/insights', // video-actions-tab
        '/manager/research/dataframes', // dataframes intro
        '/manager/research/dataframes', // df-undo
        '/manager/research/dataframes', // df-redo
        '/manager/research/dataframes', // df-history
        '/manager/research/dataframes', // df-new-sheet
        '/manager/research/dataframes', // df-open-workbooks
        '/manager/research/dataframes', // df-import
        '/manager/research/dataframes', // df-export
        '/manager/research/speedReader', // speed reader intro
        '/manager/research/speedReader', // speed-reader-text-input
        '/manager/research/speedReader', // speed-reader-summary-button
        '/manager/research/speedReader', // speed-reader-mcq-button
        '/manager/research/speedReader', // speed-reader-sessions
        '/manager/research/scenarioModeler', // map maker intro
        '/manager/research/scenarioModeler', // map-enhance-prompt
        '/manager/research/scenarioModeler', // map-generate-diagram
        '/manager/research/scenarioModeler', // map-diagram-tab
        '/manager/research/scenarioModeler', // map-flowmap-tab
        '/manager/research/scenarioModeler', // map-download-svg
        '/manager/research/scenarioModeler', // map-send-blueprint
        '/manager/research/blueprint', // blueprint intro
        '/manager/research/blueprint', // blueprint-objective-input
        '/manager/research/blueprint', // blueprint-attach-files
        '/manager/research/blueprint', // blueprint-generate
        '/manager/research/blueprint', // blueprint-right-panel
        '/manager/research/toolchain', // toolchain intro
        '/manager/research/toolchain', // toolchain-search
        '/manager/research/toolchain', // toolchain-templates
        '/manager/research/toolchain', // toolchain-clear
        '/manager/research/toolchain', // toolchain-builder
        '/manager/research/toolchain', // toolchain-save
        '/manager/research/toolchain', // toolchain-run
        '/manager/research/advisor', // advisor intro
        '/manager/research/advisor', // advisor-tools-panel
        '/manager/research/advisor', // advisor-language-select
        '/manager/research/advisor', // advisor-input-text
        '/manager/research/advisor', // advisor-attach-files
        '/manager/research/advisor', // advisor-process-text
        '/manager/research/advisor', // advisor-output-text
        '/manager/clients/scheduling', // clients scheduling intro
        '/manager/clients/directory', // clients directory intro
        '/manager/clients/directory', // clients-import-button
        '/manager/clients/directory', // clients-export-button
        '/manager/clients/directory', // clients-add-client-button
        '/manager/notes', // notes-section
        '/manager/profile/general', // profile general
        '/manager/profile/security', // profile security
        '/manager/profile/notifications', // profile notifications
        '/manager/profile/onboarding' // profile onboarding
      ],
      consultant: [
        '/consultant/dashboard', // dashboard-welcome
        '/consultant/dashboard', // header-sidebar
        '/consultant/dashboard', // header-project-selector
        '/consultant/dashboard', // header-notifications
        '/consultant/dashboard', // header-messages
        '/consultant/dashboard', // header-notes-icon
        '/consultant/dashboard', // header-pomodoro-timer
        '/consultant/dashboard', // header-three-dots-menu
        '/consultant/dashboard', // header-avatar
        '/consultant/dashboard', // projects-section
        '/consultant/dashboard', // upcoming-items
        '/consultant/dashboard', // project-tasks
        '/consultant/dashboard', // recent-activity
        '/consultant/dashboard', // project-team
        '/consultant/dashboard', // assigned-clients
        '/consultant/projects', // create-project (projects page)
        '/consultant/projects', // create-project-button
        '/consultant/calendar', // calendar-section
        '/consultant/research/conversationalAI', // conversational ai intro
        '/consultant/research/conversationalAI', // ai-chat-menu-toggle
        '/consultant/research/conversationalAI', // ai-chat-new-session
        '/consultant/research/conversationalAI', // ai-chat-file-panel
        '/consultant/research/conversationalAI', // ai-chat-message-input
        '/consultant/research/conversationalAI', // ai-chat-attach-files
        '/consultant/research/conversationalAI', // ai-chat-voice-input
        '/consultant/research/conversationalAI', // ai-chat-send-message
        '/consultant/research/insights', // video insights intro
        '/consultant/research/insights', // video-browse
        '/consultant/research/insights', // video-saved-insights
        '/consultant/research/insights', // video-url-input
        '/consultant/research/insights', // video-transcript-tab
        '/consultant/research/insights', // video-minutes-tab
        '/consultant/research/insights', // video-summary-tab
        '/consultant/research/insights', // video-actions-tab
        '/consultant/research/dataframes', // dataframes intro
        '/consultant/research/dataframes', // df-undo
        '/consultant/research/dataframes', // df-redo
        '/consultant/research/dataframes', // df-history
        '/consultant/research/dataframes', // df-new-sheet
        '/consultant/research/dataframes', // df-open-workbooks
        '/consultant/research/dataframes', // df-import
        '/consultant/research/dataframes', // df-export
        '/consultant/research/speedReader', // speed reader intro
        '/consultant/research/speedReader', // speed-reader-text-input
        '/consultant/research/speedReader', // speed-reader-summary-button
        '/consultant/research/speedReader', // speed-reader-mcq-button
        '/consultant/research/speedReader', // speed-reader-sessions
        '/consultant/research/scenarioModeler', // map maker intro
        '/consultant/research/scenarioModeler', // map-enhance-prompt
        '/consultant/research/scenarioModeler', // map-generate-diagram
        '/consultant/research/scenarioModeler', // map-diagram-tab
        '/consultant/research/scenarioModeler', // map-flowmap-tab
        '/consultant/research/scenarioModeler', // map-download-svg
        '/consultant/research/scenarioModeler', // map-send-blueprint
        '/consultant/research/blueprint', // blueprint intro
        '/consultant/research/blueprint', // blueprint-objective-input
        '/consultant/research/blueprint', // blueprint-attach-files
        '/consultant/research/blueprint', // blueprint-generate
        '/consultant/research/blueprint', // blueprint-right-panel
        '/consultant/research/toolchain', // toolchain intro
        '/consultant/research/toolchain', // toolchain-search
        '/consultant/research/toolchain', // toolchain-templates
        '/consultant/research/toolchain', // toolchain-clear
        '/consultant/research/toolchain', // toolchain-builder
        '/consultant/research/toolchain', // toolchain-save
        '/consultant/research/toolchain', // toolchain-run
        '/consultant/research/advisor', // advisor intro
        '/consultant/research/advisor', // advisor-tools-panel
        '/consultant/research/advisor', // advisor-language-select
        '/consultant/research/advisor', // advisor-input-text
        '/consultant/research/advisor', // advisor-attach-files
        '/consultant/research/advisor', // advisor-process-text
        '/consultant/research/advisor', // advisor-output-text
        '/consultant/clients/scheduling', // clients scheduling intro
        '/consultant/clients/directory', // clients directory intro
        '/consultant/clients/directory', // clients-import-button
        '/consultant/clients/directory', // clients-export-button
        '/consultant/clients/directory', // clients-add-client-button
        '/consultant/notes', // notes-section
        '/consultant/profile/general', // profile general
        '/consultant/profile/security', // profile security
        '/consultant/profile/notifications', // profile notifications
        '/consultant/profile/onboarding' // profile onboarding
      ],
      client: [
        '/clientportal/dashboard', // dashboard-welcome
        '/clientportal/dashboard', // project-identity
        '/clientportal/dashboard', // project-team
        '/clientportal/dashboard', // mission-statement
        '/clientportal/dashboard', // project-files
        '/clientportal/dashboard', // project-activity
        '/clientportal/calendar', // calendar-section
        '/clientportal/billing', // billing-section
      ],
      business: [
        `${basePath}/dashboard`, // 1. business-welcome (body)
        `${basePath}/dashboard`, // 2. business-sidebar
        `${basePath}/dashboard`, // 3. business-upcoming-appointments
        `${basePath}/dashboard`, // 4. business-recent-conversations
        `${basePath}/appointments`, // 4. business-calendar-views
        `${basePath}/appointments`, // 5. business-calendar-grid
        `${basePath}/appointments`, // 6. business-calendar-tab-unavailability
        `${basePath}/chat`, // 7. business-chat-list
        `${basePath}/chat`, // 8. business-chat-area
        `${basePath}/contacts`, // 9. business-contacts-search
        `${basePath}/contacts`, // 10. business-contacts-settings
        `${basePath}/contacts`, // 11. business-contacts-add
        `${basePath}/contacts`, // 12. business-contacts-import
        `${basePath}/contacts`, // 13. business-contacts-export
        `${basePath}/settings/chatbot/general`, // 14. business-chatbot-tab-general
        `${basePath}/settings/chatbot/prompts`, // 15. business-chatbot-tab-prompts
        `${basePath}/settings/chatbot/hours`, // 16. business-chatbot-tab-hours
        `${basePath}/profile/onboarding`, // 17. profile-settings
      ]
    };

    const routes = stepRoutes[role as keyof typeof stepRoutes] || stepRoutes.consultant;
    return routes[stepIndex] || `${basePath}/dashboard`;
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    // Handle navigation based on step
    if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + 1;
      // We just advance the step here. The Effect will handle navigation and waiting.
      // This decouples the Joyride event from the async navigation process.
      setJoyrideRun(false); // Pause instantly to avoid "Target not found" while moving
      nextStep();
    }

    // If target not found, check if it's a conditional element that might not be mounted
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn(`OnboardingTour: Target element not found for step ${index}. Retrying via Effect...`);
      // If we got here, it means the Effect thought we were ready but Joyride failed.
      // We can try to pause and resume to re-trigger search
      setJoyrideRun(false);
    }

    // Handle completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      if (status === STATUS.SKIPPED) {
        skipOnboarding();
      } else {
        completeOnboarding();
      }
      setJoyrideRun(false);
      setTourStarted(false); // Allow tour to restart if needed
    }
  };

  // Don't render if no steps or not active
  if (!steps.length || (!joyrideRun && !isOnboardingActive)) {
    // We render null, BUT we need the component to stay mounted to run effects if OnboardingActive is true.
    // If OnboardingActive is true but joyrideRun is false, we return null to hide the tour
    // but the component itself (OnboardingTour) needs to be rendered by the parent App.
    // Wait, if we return null, the effects in THIS component will unmount. 
    // We must return <></> (fragment) or similar if we want effects to run?
    // No, if the parent conditionally renders <OnboardingTour />, then returning null here is fine *if* only visual.
    // But hooks run before return. So returning null is fine for visual hiding.
    // BUT if the parent unmounts OnboardingTour when !joyrideRun, that's bad.
    // The parent uses <OnboardingTour /> unconditionally. So returning null here is strictly visual.
    if (!isOnboardingActive) return null;
    if (!joyrideRun) return null; // Visually hide, but effects still run? NO.
    // If we return null, React unmounts the component instance's VDOM? 
    // NO. Functional components execute the body. If they return null, they just don't produce DOM.
    // The effects defined inside WILL still run as long as the component is mounted by the parent.
    // CONFIRMED: Returning null from a component does NOT stop its effects from running.
    // However, Joyride component will unmount if we return null. That's what we want (pause).
  }

  // Actually, we should probably output nothing if !isOnboardingActive.
  if (!isOnboardingActive) return null;

  return (
    <Joyride
      steps={steps}
      run={joyrideRun}

      stepIndex={currentStep}
      callback={handleJoyrideCallback}
      continuous={true}
      showProgress={false}
      showSkipButton={true}
      hideCloseButton={true}
      hideBackButton={true}
      disableOverlayClose={true}
      spotlightClicks={false}
      disableScrollParentFix={true}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          textColor: 'hsl(var(--foreground))',
          backgroundColor: 'hsl(var(--background))',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
          beaconSize: 36,
          zIndex: 100,
        },
        tooltip: {
          borderRadius: 8,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: 'hsl(var(--primary))',
          fontSize: 14,
          borderRadius: 6,
          color: 'hsl(var(--primary-foreground))',
        },
        buttonBack: {
          color: 'hsl(var(--muted-foreground))',
          marginLeft: 'auto',
          marginRight: 8,
        },
        buttonSkip: {
          color: 'hsl(var(--muted-foreground))',
        },
        buttonClose: {
          height: 14,
          width: 14,
          top: 15,
          right: 15,
        },
      }}
      locale={{
        back: 'Previous',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        open: 'Open the dialog',
        skip: 'Skip tour',
      }}
    />
  );
};

export default OnboardingTour;
